import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { Button } from '@monorepo/nova-ui';
import { useDashboard } from '../../../../../../contexts';
import { useAccountSelector } from '../hooks';
import {
  ErrorContainer,
  LoadingContainer,
  ProgramListContainer,
  ProgramListItem,
} from '../styled';
import { ProgramListProps } from '../types';
import { ProgramItem } from './program-item';

export const ProgramList: FC<ProgramListProps> = ({ onClose }) => {
  const { t } = useTranslation('dashboardHeader');
  const { account: currentAccount } = useDashboard();

  const {
    accounts,
    getProgramById,
    isLoading,
    error,
    refetch,
    navigateToOverview,
  } = useAccountSelector(onClose);

  // Determine if an account is the current one
  const isCurrentAccount = (accountId: string) => {
    if (!currentAccount) return false;
    const currentAccountId =
      currentAccount.tsAccountId || currentAccount._id || '';
    return accountId === currentAccountId;
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          {t('programSelector.loadingPrograms')}
        </Typography>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <Typography variant="body2" className="error-message">
          {t('programSelector.failedToLoadPrograms')}
        </Typography>
        <Button variant="outlined" size="sm" onClick={() => refetch()}>
          {t('programSelector.retry')}
        </Button>
      </ErrorContainer>
    );
  }

  // Empty state when no programs are available
  if (accounts.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={(theme) => ({
          color: theme.palette.text.secondary,
          textAlign: 'center',
          py: 4,
        })}
      >
        {t('programSelector.emptyState')}
      </Typography>
    );
  }

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="subtitle2"
          sx={(theme) => ({
            color: theme.palette.text.main,
            fontWeight: theme.typography.fontWeightRegular,
            mb: 1,
          })}
        >
          {t('programSelector.myProgram')}
        </Typography>

        <Button variant="link" size="sm" onClick={navigateToOverview}>
          {t('programSelector.viewAll')}
        </Button>
      </Stack>

      <ProgramListContainer>
        <ProgramListItem>
          {accounts.map((account, index) => {
            const program = getProgramById(account.programId);
            if (!program) {
              return null;
            }
            const accountId = account.tsAccountId || account._id || '';
            const isCurrent = isCurrentAccount(accountId);
            return (
              <ProgramItem
                key={index}
                account={account}
                program={program}
                onClose={onClose}
                isCurrent={isCurrent}
              />
            );
          })}
        </ProgramListItem>
      </ProgramListContainer>
    </>
  );
};
